import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostBinding, Inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { RacingOptionMarketTypes } from '@frontend/sports/racing/feature/models';
import { Store } from '@ngrx/store';
import { combineLatest, filter, tap, withLatestFrom } from 'rxjs';

import { IBetslipMessages } from '../../../../base/models/betslip-messages';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { selectIsLinearBetslip } from '../../../../base/store/selectors';
import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipType } from '../../../../core/betslip-type';
import { BetslipV2WinParticipantPick } from '../../../../core/picks/betslip-v2-win-participant-pick';
import { PickOddsState } from '../../../../core/picks/pick-models';
import { selectedPicksByCurrentTypeSelector } from '../../../betslip-bar/selectors';
import BetslipSingleBetActions from '../../../single-bet/actions';
import { ISingleBetPickState } from '../../../single-bet/state';
import { BetslipTypesActions } from '../../../types/actions';
import { betslipTypeStateSelector, selectIsSingleBetSelected } from '../../../types/selectors';
import { selectHasCurrentComboPreventionError } from '../../../validation/selectors';
import BetslipPickUtilsService from '../../services/betslip-pick-utils.service';

@Component({
    selector: 'bs-eachway-price-button',
    templateUrl: './eachway-price-button.component.html',
    styleUrls: ['eachway-price-button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class EachwayPriceButtonComponent implements OnInit {
    @Input() pick: BetslipV2WinParticipantPick;
    @Input() betslipType?: BetslipType;

    @HostBinding('class.deselected')
    get isPickDeselected(): boolean {
        return this.pickState !== undefined && !this.pickState.isSelected;
    }

    isSinglePick = false;
    isEachWaySelected = false;
    pickState: ISingleBetPickState | undefined;

    messages: IBetslipMessages;

    constructor(
        private store: Store<IBetslipRootState>,
        private betslipPickUtilsService: BetslipPickUtilsService,
        private changeDetectorRef: ChangeDetectorRef,
        private destroyRef: DestroyRef,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
    ) {
        this.messages = messageProvider.getMessages();
    }

    ngOnInit(): void {
        const types$ = this.store.select(betslipTypeStateSelector);
        const isLinear$ = this.store.select(selectIsLinearBetslip);

        combineLatest([types$, isLinear$])
            .pipe(
                filter((pick) => !!pick),
                tap(([state, isLinear]) => {
                    const betslipType = this.betslipType || state.base.currentSelectedType;
                    this.isSinglePick = betslipType === BetslipType.Single;
                    this.pickState = this.isSinglePick ? state.singleBet.picks[this.pick.id.toString()] : undefined;
                    this.isEachWaySelected = this.betslipPickUtilsService.isEachWaySelected(state, betslipType, this.pickState, isLinear);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.store
            .select(betslipTypeStateSelector)
            .pipe(
                withLatestFrom(this.store.select(selectedPicksByCurrentTypeSelector), this.store.select(selectHasCurrentComboPreventionError)),
                filter(([_, __, hasComboPrevention]) => !hasComboPrevention),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(([typeState, pickState]) => {
                const isEachWaySelected =
                    typeState.base.currentSelectedType! === BetslipType.Combo ? typeState.comboBet.isEachWay : typeState.systemBet.isEachWay;
                const isEachwayExtra = pickState.some((p) => p.market.marketType?.toString() === RacingOptionMarketTypes.WinEwExtraPlace);
                const isEachWay =
                    isEachwayExtra && typeState.base.currentSelectedType !== BetslipType.Single
                        ? { isEachWay: true }
                        : { isEachWay: isEachWaySelected };

                this.store.dispatch(BetslipTypesActions.setEachWay(isEachWay));
                this.changeDetectorRef.markForCheck();
            });

        this.store
            .select(selectIsSingleBetSelected)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (!this.pickState) {
                    return;
                } else if (!this.pickState.isEachWay && this.isEachWayExtra) {
                    this.store.dispatch(BetslipSingleBetActions.toggleSingleBetPickEachWay({ pickId: this.pick.id }));
                }
            });
    }

    onEachwayButtonClick(event: MouseEvent) {
        event.stopPropagation();

        if (!this.pickState?.isSelected || this.pick.oddsState === PickOddsState.Locked) {
            return;
        }

        this.store.dispatch(BetslipSingleBetActions.toggleSingleBetPickEachWay({ pickId: this.pick.id }));
    }

    get isEachWayExtra(): boolean {
        return this.pick.market?.marketType?.toString() === RacingOptionMarketTypes.WinEwExtraPlace;
    }

    get eachWayTerms(): string {
        if (this.pick.isEachWay) {
            const placeTerms = this.pick.market.placeTerms!;
            const placesText = Array.from(Array(placeTerms.places), (x, i) => i + 1).join('-');

            return `${placeTerms.numerator}/${placeTerms.denominator} ${placesText}`;
        }

        return '';
    }

    get showEachWayButton(): boolean {
        return this.pick.market?.marketType?.toString() === RacingOptionMarketTypes.WinEwExtraPlace || this.isSinglePick;
    }

    get showEachWayText(): boolean {
        return this.pick.market?.marketType?.toString() === RacingOptionMarketTypes.WinEwExtraPlace || (!!this.eachWayTerms && this.isSinglePick);
    }

    get showEachWayInColumn(): boolean {
        return this.isEachWayExtra ? false : this.isEachWaySelected && !!this.eachWayTerms && !this.isSinglePick && !this.pickState?.rewardTokenId;
    }
}
